import { formatTargetDateRange } from '~/models/dateTime'
import type { SubmitConfirmSummary } from '~/pages/ProcurementPlanDetail/components/SubmitConfirmDialog'
import type { SlotValue } from '~/pages/ProcurementPlanDetail/models/calc'

/**
 * 48 コマ (またはそれ以下) の SlotValue 配列を合算する。未入力 (undefined)
 * コマは 0 として計上せず合算対象から除外する (= AC-01 重点ケースの
 * 「空セルは 0 扱いにしない = null 除外」)。
 */
export function sumSlots(slots: SlotValue[]): number {
  return slots
    .filter((v): v is number => v !== undefined)
    .reduce((acc, v) => acc + v, 0)
}

/** diffFlags を持つ列の最小形 (BreakdownColumn の部分型。countChangedSlots はこれで足りる)。 */
type ColumnWithDiffFlags = { diffFlags: boolean[] }

/**
 * 前回提出からの変更コマ数 (AC-01)。需要・調達内訳列・販売内訳列のいずれかで
 * 差分がある koma index の数を、重複なく (= 同一コマが複数ソースで差分でも 1
 * 回のみ) カウントする。
 */
export function countChangedSlots(
  demandDiffFlags: boolean[],
  procurementColumns: ColumnWithDiffFlags[],
  salesColumns: ColumnWithDiffFlags[],
): number {
  let count = 0
  for (let i = 0; i < demandDiffFlags.length; i++) {
    const changed =
      demandDiffFlags[i] === true ||
      procurementColumns.some((c) => c.diffFlags[i] === true) ||
      salesColumns.some((c) => c.diffFlags[i] === true)
    if (changed) count++
  }
  return count
}

type BuildSubmitConfirmSummaryParams = {
  /** 呼び出し側で t() 済みの計画タイプ表示名 (= 常に「需要調達計画」)。 */
  planTypeLabel: string
  targetPeriodStartDate:
    | { year: number; month: number; day: number }
    | undefined
  /**
   * 呼び出し側で convertAreaLabel() 済みのエリア表示名。 i18next 依存の
   * label 導出は component 側の責務とし、 本 model は純関数として保つ
   * (= ~/models/area/index.ts:convertAreaLabel と同じく i18n 経由の label
   * 導出はここでは行わない。 既存 labels.test.ts の precedent と同じ切り分け)。
   */
  areaLabel: string
  procurementTotals: SlotValue[]
  /**
   * 48 コマの需要計画値 (index.tsx で取得済みの demandSlots そのまま)。需要調達計画は
   * 需要 BG が 1 つのみのため、内訳列の合算を経ずそのままコマ別の需要計画値合計となる
   * (spec AC-006 の「需要計画値」項)。
   */
  demandSlots: SlotValue[]
  salesTotals: SlotValue[]
  /** 48 コマの需給バランス (calcSupplyDemandBalance の出力そのまま)。 */
  balances: SlotValue[]
  changedSlotCount: number
}

/**
 * SubmitConfirmDialog に渡すサマリーを組み立てる (AC-01)。
 *
 * [rule 逸脱] 確認画面の数量をサーバー計算値でなく client 集計で出す。
 * 提出 payload は plan_id のみで数値を送信しないため、この集計は送信内容を
 * 構成しない — detail 画面が既に client 集計で表示している値の再掲。
 */
export function buildSubmitConfirmSummary(
  params: BuildSubmitConfirmSummaryParams,
): SubmitConfirmSummary {
  const procurementTotalKwh = sumSlots(params.procurementTotals)
  const demandTotalKwh = sumSlots(params.demandSlots)
  const salesTotalKwh = sumSlots(params.salesTotals)
  const hasNegativeBalance = params.balances.some(
    (v) => v !== undefined && v < 0,
  )
  return {
    planTypeLabel: params.planTypeLabel,
    periodLabel: formatTargetDateRange(params.targetPeriodStartDate),
    areaLabel: params.areaLabel,
    procurementTotalKwh,
    salesTotalKwh,
    // spec AC-006: 調達計画値合計 − 需要計画値 − 販売計画値合計 (domain-knowledge.md §5 準拠)。
    balanceKwh: procurementTotalKwh - demandTotalKwh - salesTotalKwh,
    changedSlotCount: params.changedSlotCount,
    hasNegativeBalance,
  }
}
